import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Navigation, MapPin, Clock, Phone, Car, 
  CheckCircle, Loader2, AlertCircle, User
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicTrackingPage() {
  const { shareToken } = useParams();
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTracking = async () => {
      try {
        const response = await fetch(`${API_URL}/api/track/${shareToken}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError("This tracking link is not found or has expired.");
          } else if (response.status === 410) {
            setError("This tracking link has expired.");
          } else {
            setError("Failed to load tracking information.");
          }
          return;
        }
        
        const data = await response.json();
        setTrackingData(data);
      } catch (err) {
        setError("Failed to connect to tracking service.");
      } finally {
        setLoading(false);
      }
    };

    fetchTracking();
    
    // Refresh every 30 seconds for active rides
    const interval = setInterval(fetchTracking, 30000);
    return () => clearInterval(interval);
  }, [shareToken]);

  const getStatusInfo = (status) => {
    switch (status) {
      case "scheduled":
        return { 
          label: "Scheduled", 
          color: "bg-blue-500", 
          icon: Clock,
          description: "The ride is scheduled and waiting for the driver to start."
        };
      case "in_progress":
        return { 
          label: "Driver En Route", 
          color: "bg-amber-500", 
          icon: Car,
          description: "The driver is on their way to the pickup location."
        };
      case "arrived":
        return { 
          label: "Driver Arrived", 
          color: "bg-green-500", 
          icon: CheckCircle,
          description: "The driver has arrived at the pickup location."
        };
      case "completed":
        return { 
          label: "Completed", 
          color: "bg-slate-500", 
          icon: CheckCircle,
          description: "This ride has been completed."
        };
      default:
        return { 
          label: status, 
          color: "bg-slate-500", 
          icon: Clock,
          description: ""
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
          <p className="text-lg">Loading tracking information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white/10 backdrop-blur-xl border-white/20">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Tracking Unavailable</h2>
            <p className="text-slate-300">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (trackingData?.status === "completed") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white/10 backdrop-blur-xl border-white/20">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Ride Completed</h2>
            <p className="text-slate-300">This ride has been safely completed.</p>
            <p className="text-slate-400 text-sm mt-4">Thank you for using There You Are!</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusInfo = getStatusInfo(trackingData?.status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900">
      {/* Header */}
      <header className="bg-black/30 backdrop-blur-sm border-b border-white/10 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-xl flex items-center justify-center">
            <Navigation className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">There You Are</h1>
            <p className="text-cyan-300 text-xs">Live Ride Tracking</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Status Card */}
        <Card className="bg-white/10 backdrop-blur-xl border-white/20 overflow-hidden">
          <div className={`${statusInfo.color} px-4 py-3`}>
            <div className="flex items-center gap-3 text-white">
              <StatusIcon className="w-6 h-6" />
              <div>
                <p className="font-bold text-lg">{statusInfo.label}</p>
                <p className="text-sm opacity-90">{statusInfo.description}</p>
              </div>
            </div>
          </div>
          
          {trackingData?.eta_minutes && trackingData.status === "in_progress" && (
            <div className="px-4 py-6 text-center border-t border-white/10">
              <p className="text-slate-400 text-sm">Estimated Arrival</p>
              <p className="text-5xl font-black text-white">{trackingData.eta_minutes}</p>
              <p className="text-cyan-300 font-medium">minutes</p>
            </div>
          )}
        </Card>

        {/* Passenger Info */}
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-4">
              <User className="w-5 h-5 text-cyan-400" />
              <h3 className="font-semibold text-white">Passenger</h3>
            </div>
            <p className="text-xl font-bold text-white">{trackingData?.client_name}</p>
          </CardContent>
        </Card>

        {/* Trip Details */}
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-green-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wide">Pickup</p>
                <p className="text-white font-medium">{trackingData?.pickup_address}</p>
                <p className="text-cyan-300 text-sm mt-1">
                  {trackingData?.pickup_date} at {trackingData?.pickup_time}
                </p>
              </div>
            </div>
            
            <div className="border-l-2 border-dashed border-slate-600 ml-4 h-6"></div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-red-400" />
              </div>
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wide">Dropoff</p>
                <p className="text-white font-medium">{trackingData?.dropoff_address}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Driver Info */}
        {trackingData?.driver_name && (
          <Card className="bg-white/10 backdrop-blur-xl border-white/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-4">
                <Car className="w-5 h-5 text-cyan-400" />
                <h3 className="font-semibold text-white">Your Driver</h3>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xl font-bold text-white">{trackingData.driver_name}</p>
                  {trackingData.driver_vehicle && (
                    <p className="text-slate-300 text-sm">
                      {[
                        trackingData.driver_vehicle.color,
                        trackingData.driver_vehicle.make,
                        trackingData.driver_vehicle.model
                      ].filter(Boolean).join(" ")}
                    </p>
                  )}
                  {trackingData.driver_vehicle?.plate && (
                    <Badge className="mt-2 bg-slate-700 text-white border-0">
                      {trackingData.driver_vehicle.plate}
                    </Badge>
                  )}
                </div>
                
                {trackingData.driver_phone && (
                  <a 
                    href={`tel:${trackingData.driver_phone}`}
                    className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-400 transition-colors"
                  >
                    <Phone className="w-5 h-5 text-white" />
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Auto-refresh notice */}
        <p className="text-center text-slate-500 text-xs">
          This page updates automatically every 30 seconds
        </p>
      </main>
    </div>
  );
}
